<template>
  <div>
    <transition name="fade">
      <div v-if="!locked && pinRequired && pinTimeout">
        <div :class="prompt ? 'block' : 'hidden'" class="fixed top-0 left-1/2 transform -translate-x-1/2 mt-3 px-3 py-2 rounded-lg text-sm bg-white/25 shadow text-gray-800 leading-none flex items-center border">
          <o-icon icon="locked" class="mr-2" />
          <i18n-t keypath="components.auth.elements.lock.inactivitySessionLock" tag="p" scope="global">
            <!-- TODO: Add back in when v-idle is fixed -->
            <!--<template #seconds>
              <span class="font-semibold mx-1"><v-idle
                :reminders="[15, pinTimeout]"
                :duration="pinTimeout"
                class="inline"
                @remind="onRemind"
                @idle="onIdle"
              />{{ $t('components.auth.elements.lock.seconds') }}</span>
            </template>-->
          </i18n-t>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div
        v-if="locked"
        id="lock"
        class="fixed inset-0 h-screen w-screen z-80000 bg-gray-900/15 backdrop-blur-md"
        tabindex="0"
        @contextmenu="handleContextMenu"
        @keydown="handleKeyDown"
      >
        <div class="h-screen flex-col items-center justify-center">
          <div class="h-full flex items-center justify-center flex-grow z-10 sm:px-6 lg:px-8">
            <div class="max-w-md w-full h-full sm:h-auto bg-white/75 dark:bg-gray-900 dark:border dark:border-gray-800 py-12 sm:py-16 px-12 sm:px-24 sm:rounded-2xl sm:mb-32 backdrop-blur shadow-lg">
              <div>
                <div v-if="active" class="mb-12 mt-2">
                  <div v-if="active.info.employee.images" class="w-36 h-36 mx-auto rounded-full p-1 mb-6">
                    <img
                      v-if="active.info.employee.images"
                      :src="active.info.employee.images.large"
                      class="h-32 w-32 object-cover rounded-full bg-gray-100 border border-white"
                    >
                    <div v-else class="pointer-events-none w-full h-full rounded-full bg-gray-200 border border-white">
                      <p>{{ active.info.employee.displayName }}</p>
                    </div>
                  </div>
                  <h2 class="text-center text-3xl sm:text-3xl leading-9 font-extrabold opacity-90">
                    {{ active.info.employee.displayName }}
                  </h2>
                  <i18n-t v-if="userHasPin" keypath="auth.pin.enter" tag="p" class="mt-3 opacity-70 text-center leading-5 text-sm" scope="global">
                    <template #switchUser>
                      <o-button
                        tag="nuxt-link"
                        to="/auth/switch"
                        size="xs"
                        variant="primary"
                        flat
                        slim
                      >
                        {{ $t('auth.pin.switchUser') }}
                      </o-button>
                    </template>
                  </i18n-t>

                  <p v-if="!userHasPin" class="mt-3 text-gray-600 text-center leading-5 text-sm">
                    <i18n-t keypath="components.auth.elements.lock.unlockOrSwitchUser" scope="global">
                      <template #switchUser>
                        <o-button
                          tag="nuxt-link"
                          to="/auth/switch"
                          size="xs"
                          variant="primary"
                          flat
                          outline
                          slim
                        >
                          {{ $t('auth.pin.switchUser') }}
                        </o-button>
                      </template>
                    </i18n-t>
                  </p>
                </div>
                <div v-if="active">
                  <div v-if="userHasPin" class="mx-12 relative">
                    <input
                      ref="field"
                      v-model="pin"
                      :aria-label="$t('auth.pin.label')"
                      name="password"
                      type="password"
                      required
                      class="field appearance-none font-light relative block w-full px-3 py-2 "
                      :class="[
                        'tracking-widest rounded-xl shadow placeholder-gray-500 focus:outline-none focus:z-10 sm:leading-5 bg-white/75 dark:bg-gray-950/50 text-3xl',
                        error ? 'bg-red-50 border-red-400 dark:border-red-950 text-red-700 focus:border-red-300 focus:shadow-outline-red ring-4 ring-offset-0 ring-red-200 dark:ring-red-950' : 'border-gray-300 text-gray-800 dark:text-white/90 focus:border-primary-300 focus:shadow-outline-blue',
                        { 'animate-shake' : animate }
                      ]"
                      @keydown.enter="handleUnlock"
                    >

                    <o-button icon="arrowRight" flat class="absolute top-1/2 right-0 transform -translate-y-1/2 mx-3 z-10" @click="handleUnlock" />
                  </div>

                  <div v-if="!userHasPin" class="mx-12 relative">
                    <o-button icon="locked" size="lg" variant="primary" class="w-full" @click="handleUnlock">
                      {{ $t('components.auth.elements.lock.unlock') }}
                    </o-button>
                  </div>

                  <div v-if="attempts" class="text-sm text-red-700 text-center mt-2">
                    {{ $t('auth.attemptsRemaining', {count: 5 - attempts}) }}
                  </div>

                  <div v-if="userHasPin" class="mt-14 flex items-center justify-center space-x-2">
                    <o-button size="xs" variant="success" outline @click="handleChangePin">
                      {{ $t('auth.pin.changeDisable') }}
                    </o-button>

                    <o-button v-if="admin" size="xs" variant="success" outline @click="handleAdminUnlock">
                      {{ $t('auth.adminUnlock') }}
                    </o-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <ChangePinDialog
      ref="changePinDialogRef"
      @success="handleChangePinSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useNuxtApp } from '#app'
import { useAuthStore } from '~/stores/useAuthStore'

import ChangePinDialog from '@/components/auth/actions/ChangePin.vue'

const { $auth } = useNuxtApp()
const authStore = useAuthStore()

const field = ref<any>()
const changePinDialogRef = ref<any>()
const pin = ref('')
const error = ref(false)
const animate = ref(false)
const prompt = ref(false)
const observer = ref<MutationObserver | null>(null)

const active = computed(() => authStore.activeAccount)
const locked = computed(() => authStore.locked)
const attempts = computed(() => authStore.attempts)

const pinTimeout = computed(() => {
  return active.value?.info?.location?.webPINTimeout
})

const pinRequired = computed(() => {
  return active.value?.info?.location?.webPINRequired
})

const userHasPin = computed(() => {
  return !!active.value?.info?.employee?.pinHash
})

const admin = computed(() => {
  return active.value?.info?.adminUser
})

watch(locked, (value) => {
  pin.value = ''
  error.value = false
  animate.value = false

  if (value) {
    nextTick(() => {
      field.value?.focus()
      createObserver()
    })
  } else {
    destroyObserver()
  }
}, { immediate: true })

function handleChangePin () {
  changePinDialogRef.value?.open()
}

function handleChangePinSuccess () {
  $auth.unlock({ override: true, sync: true })
}

function handleAdminUnlock () {
  $auth.unlock({ override: true })
}

async function handleUnlock () {
  if (userHasPin.value) {
    if (pin.value) {
      try {
        await $auth.unlock({ pin: pin.value, sync: false })
      } catch (err) {
        animate.value = true
        error.value = true

        $auth.setAttempts(attempts.value + 1)

        if (attempts.value === 5) {
          $auth.logout()
        }
      }

      setTimeout(() => {
        animate.value = false
      }, 1000)
    }
  } else {
    $auth.unlock({ override: true })
  }
}

function handleKeyPress (key: string) {
  pin.value += key
}

function handleBackspace () {
  pin.value = pin.value.slice(0, -1)
}

function handleContextMenu (e: Event) {
  e.preventDefault()
}

function handleKeyDown (e: KeyboardEvent) {
  const F12 = 123
  const F5 = 116
  const F7 = 118
  const U = 85

  const keys = [73, 67, 74, 75, 83, 69, 77]

  const { keyCode, shiftKey, ctrlKey } = e

  if (keyCode === F12 ||
    (shiftKey && ctrlKey && keys.includes(keyCode)) ||
    (shiftKey && [F5, F7].includes(keyCode)) ||
    (ctrlKey && keyCode === U)) {
    e.preventDefault()
  }
}

function onRemind (t: number) {
  if (t === 15) {
    prompt.value = true
  } else {
    prompt.value = false
  }
}

function onIdle (t: number) {
  $auth.lock()
}

function createObserver () {
  const config = {
    attributes: true,
    childList: true,
    subtree: true,
    attributeOldValue: true,
    characterData: true,
  }

  const callback = (records: MutationRecord[]) => {
    nextTick(() => {
      let reload = false

      records.forEach((record) => {
        if (record.type === 'attributes' && ((record.target as Element).matches && (record.target as Element).matches('body, #content, #lock')) && record.attributeName === 'style') {
          reload = true
        }
        if (record.type === 'characterData') {
          if ((record.target as any).parentNode.tagName.toLowerCase() === 'style') {
            reload = true
          }
        }
        record.removedNodes.forEach((node) => {
          if ((node as Element).id === 'lock') {
            reload = true
          }
        })

        if (reload) {
          window.location.reload()
        }
      })
    })
  }

  const obs = new MutationObserver(callback)
  obs.observe(document.documentElement, config)

  observer.value = obs
}

function destroyObserver () {
  if (observer.value) {
    observer.value.disconnect()
  }
}
</script>
